import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calculator, Plus, Edit2, Trash2, X, Truck, DollarSign,
  Calendar, Package, TrendingUp, AlertCircle, Play, Pause, CheckCircle, Clock, CalendarDays
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, ReferenceLine
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#F97316', '#3B82F6', '#22C55E', '#EAB308', '#8B5CF6', '#EC4899', '#14B8A6', '#F43F5E'];

const DAYS_OF_WEEK = [
  { key: "Mon", label: "Mon" },
  { key: "Tue", label: "Tue" },
  { key: "Wed", label: "Wed" },
  { key: "Thu", label: "Thu" },
  { key: "Fri", label: "Fri" },
  { key: "Sat", label: "Sat" },
  { key: "Sun", label: "Sun" }
];

const ALL_DAYS = DAYS_OF_WEEK.map(d => d.key);

const PROJECT_STATUSES = [
  { key: "active", label: "Active", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  { key: "upcoming", label: "Upcoming", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { key: "paused", label: "Paused", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { key: "completed", label: "Completed", color: "bg-white/10 text-white/40 border-white/20" }
];

const getStatusConfig = (status) => PROJECT_STATUSES.find(s => s.key === status) || PROJECT_STATUSES[0];

// Get today's date in YYYY-MM-DD format
const getTodayString = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

export default function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview, timeline
  
  // Form state
  const [formData, setFormData] = useState({
    project_name: "",
    project_type: "volume",
    status: "active",
    start_date: getTodayString(),
    price_per_m3: "",
    total_volume: "",
    daily_volume: "",
    volume_per_load: "7.5",
    price_per_load: "",
    loads_per_day: "",
    end_date: "",
    disposal_days: ALL_DAYS,
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, summaryRes, timelineRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/projects/summary`),
        axios.get(`${API}/projects/timeline`)
      ]);
      setProjects(projectsRes.data);
      setSummary(summaryRes.data);
      setTimeline(timelineRes.data);
    } catch (error) {
      console.error("Error fetching projects data:", error);
      toast.error("Failed to load projects data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      project_name: "",
      project_type: "volume",
      status: "active",
      start_date: getTodayString(),
      price_per_m3: "",
      total_volume: "",
      daily_volume: "",
      volume_per_load: "7.5",
      price_per_load: "",
      loads_per_day: "",
      end_date: "",
      disposal_days: ALL_DAYS,
      notes: ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      project_name: formData.project_name,
      project_type: formData.project_type,
      status: formData.status,
      start_date: formData.start_date || null,
      disposal_days: formData.disposal_days.length > 0 ? formData.disposal_days : ALL_DAYS,
      notes: formData.notes || null
    };

    if (formData.project_type === "volume") {
      payload.price_per_m3 = formData.price_per_m3 ? parseFloat(formData.price_per_m3) : null;
      payload.total_volume = formData.total_volume ? parseFloat(formData.total_volume) : null;
      payload.daily_volume = formData.daily_volume ? parseFloat(formData.daily_volume) : null;
      payload.volume_per_load = formData.volume_per_load ? parseFloat(formData.volume_per_load) : 7.5;
    } else {
      payload.price_per_load = formData.price_per_load ? parseFloat(formData.price_per_load) : null;
      payload.loads_per_day = formData.loads_per_day ? parseInt(formData.loads_per_day) : null;
      payload.end_date = formData.end_date || null;
    }

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, payload);
        toast.success("Project updated");
      } else {
        await axios.post(`${API}/projects`, payload);
        toast.success("Project created");
      }
      setShowAddModal(false);
      setEditingProject(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error("Failed to save project");
    }
  };

  const handleEdit = (project) => {
    setFormData({
      project_name: project.project_name || "",
      project_type: project.project_type || "volume",
      status: project.status || "active",
      start_date: project.start_date || getTodayString(),
      price_per_m3: project.price_per_m3?.toString() || "",
      total_volume: project.total_volume?.toString() || "",
      daily_volume: project.daily_volume?.toString() || "",
      volume_per_load: project.volume_per_load?.toString() || "7.5",
      price_per_load: project.price_per_load?.toString() || "",
      loads_per_day: project.loads_per_day?.toString() || "",
      end_date: project.end_date || "",
      disposal_days: project.disposal_days || ALL_DAYS,
      notes: project.notes || ""
    });
    setEditingProject(project);
    setShowAddModal(true);
  };

  const handleDelete = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success("Project deleted");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  const handleStatusChange = async (project, newStatus) => {
    try {
      await axios.put(`${API}/projects/${project.id}`, { status: newStatus });
      toast.success(`Project marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Toggle disposal day selection
  const toggleDisposalDay = (day) => {
    setFormData(prev => {
      const days = prev.disposal_days.includes(day)
        ? prev.disposal_days.filter(d => d !== day)
        : [...prev.disposal_days, day];
      return { ...prev, disposal_days: days };
    });
  };

  const selectAllDays = () => setFormData(prev => ({ ...prev, disposal_days: ALL_DAYS }));
  const selectWeekdays = () => setFormData(prev => ({ ...prev, disposal_days: ["Mon", "Tue", "Wed", "Thu", "Fri"] }));

  // Calculate preview values for the form
  const getFormPreview = () => {
    const workingDays = formData.disposal_days.length || 7;
    
    if (formData.project_type === "volume") {
      const dailyVol = parseFloat(formData.daily_volume) || 0;
      const volPerLoad = parseFloat(formData.volume_per_load) || 7.5;
      const totalVol = parseFloat(formData.total_volume) || 0;
      const priceM3 = parseFloat(formData.price_per_m3) || 0;
      
      const loadsPerDay = volPerLoad > 0 ? Math.ceil(dailyVol / volPerLoad) : 0;
      let daysNeeded = null;
      if (dailyVol > 0 && totalVol > 0) {
        const totalWorkingDays = Math.ceil(totalVol / dailyVol);
        if (workingDays < 7) {
          const weeksNeeded = totalWorkingDays / workingDays;
          daysNeeded = Math.ceil(weeksNeeded * 7);
        } else {
          daysNeeded = totalWorkingDays;
        }
      }
      const dailyRevenue = dailyVol * priceM3;
      
      return { loadsPerDay, daysNeeded, dailyRevenue, workingDays };
    } else {
      const loads = parseInt(formData.loads_per_day) || 0;
      const priceLoad = parseFloat(formData.price_per_load) || 0;
      const dailyRevenue = loads * priceLoad;
      
      return { loadsPerDay: loads, daysNeeded: null, dailyRevenue, workingDays };
    }
  };

  const preview = getFormPreview();

  // Prepare chart data
  const chartData = projects
    .filter(p => p.status === "active")
    .map(p => ({
      name: p.project_name.length > 15 ? p.project_name.substring(0, 15) + '...' : p.project_name,
      loads: p.calculated_loads_per_day,
      revenue: p.daily_revenue
    }));

  // Create a map of project names to their "is_working_today" status from summary
  const workingTodayMap = {};
  summary?.projects?.forEach(p => {
    workingTodayMap[p.project_name] = p.is_working_today;
  });

  // Merge is_working_today into projects
  const projectsWithTodayStatus = projects.map(p => ({
    ...p,
    is_working_today: workingTodayMap[p.project_name] || false
  }));

  // Prepare timeline chart data (next 30 days)
  const timelineChartData = timeline?.timeline?.slice(0, 30).map(day => ({
    date: day.date.slice(5), // MM-DD format
    loads: day.total_loads,
    projects: day.active_projects
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="projects-loading">
        <div className="text-white/60 font-body">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-[#161920] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-8 h-8 text-primary" strokeWidth={2} />
            <span className="font-mono text-3xl font-bold text-white">{summary?.total_projects || 0}</span>
          </div>
          <p className="text-sm font-body text-white/60 uppercase tracking-wider">Total Projects</p>
        </div>
        <div className="bg-[#161920] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-2">
            <Play className="w-8 h-8 text-green-400" strokeWidth={2} />
            <span className="font-mono text-3xl font-bold text-green-400">{summary?.status_counts?.active || 0}</span>
          </div>
          <p className="text-sm font-body text-white/60 uppercase tracking-wider">Active</p>
        </div>
        <div className="bg-[#161920] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-2">
            <Truck className="w-8 h-8 text-accent-success" strokeWidth={2} />
            <span className="font-mono text-3xl font-bold text-accent-success">{summary?.today_loads || 0}</span>
          </div>
          <p className="text-sm font-body text-white/60 uppercase tracking-wider">Today's Loads ({summary?.today_day_name})</p>
        </div>
        <div className="bg-[#161920] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 text-accent-warning" strokeWidth={2} />
            <span className="font-mono text-2xl font-bold text-accent-warning">${(summary?.today_revenue || 0).toLocaleString()}</span>
          </div>
          <p className="text-sm font-body text-white/60 uppercase tracking-wider">Today's Revenue</p>
        </div>
        <div className="bg-[#161920] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-2">
            <Calculator className="w-8 h-8 text-accent-info" strokeWidth={2} />
            <span className="font-mono text-xl font-bold text-accent-info">
              {summary?.volume_projects || 0} / {summary?.load_projects || 0}
            </span>
          </div>
          <p className="text-sm font-body text-white/60 uppercase tracking-wider">Volume / Load</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveTab("overview")}
          className={`px-4 py-2 font-heading font-bold text-sm uppercase ${
            activeTab === "overview" ? "text-primary border-b-2 border-primary" : "text-white/60 hover:text-white"
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("timeline")}
          className={`px-4 py-2 font-heading font-bold text-sm uppercase ${
            activeTab === "timeline" ? "text-primary border-b-2 border-primary" : "text-white/60 hover:text-white"
          }`}
        >
          <CalendarDays className="w-4 h-4 inline mr-2" />
          Timeline
        </button>
      </div>

      {activeTab === "overview" && (
        <>
          {/* Active Projects Chart */}
          {chartData.length > 0 && (
            <div className="bg-[#161920] border border-white/10 p-6">
              <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Active Projects - Loads Per Day</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" stroke="#666" tick={{ fill: '#999' }} />
                    <YAxis type="category" dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 11 }} width={120} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1d24', border: '1px solid #333', borderRadius: 0, color: '#fff' }} 
                      labelStyle={{ color: '#fff' }} 
                      itemStyle={{ color: '#fff' }}
                      formatter={(value, name) => [name === 'loads' ? `${value} loads` : `$${value.toLocaleString()}`, name === 'loads' ? 'Loads/Day' : 'Daily Revenue']}
                    />
                    <Bar dataKey="loads" fill="#22C55E" name="Loads/Day" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Projects Table */}
          <div className="bg-[#161920] border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg uppercase text-white">All Projects</h3>
              <button
                onClick={() => { resetForm(); setEditingProject(null); setShowAddModal(true); }}
                className="px-4 h-9 bg-primary text-white font-heading font-bold text-xs uppercase flex items-center gap-2"
                data-testid="add-project-btn"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Add Project
              </button>
            </div>

            {projectsWithTodayStatus.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No projects yet. Add your first project to start tracking workload.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-2 text-white/60 font-semibold uppercase text-xs">Project</th>
                      <th className="text-left py-3 px-2 text-white/60 font-semibold uppercase text-xs">Status</th>
                      <th className="text-left py-3 px-2 text-white/60 font-semibold uppercase text-xs">Start</th>
                      <th className="text-left py-3 px-2 text-white/60 font-semibold uppercase text-xs">Days</th>
                      <th className="text-right py-3 px-2 text-white/60 font-semibold uppercase text-xs">Loads/Day</th>
                      <th className="text-right py-3 px-2 text-white/60 font-semibold uppercase text-xs">Revenue/Day</th>
                      <th className="text-left py-3 px-2 text-white/60 font-semibold uppercase text-xs">Progress</th>
                      <th className="text-center py-3 px-2 text-white/60 font-semibold uppercase text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectsWithTodayStatus.map((project) => {
                      const statusConfig = getStatusConfig(project.status);
                      return (
                        <tr key={project.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <div className="text-white font-medium">{project.project_name}</div>
                              {project.is_working_today && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-green-500/20 text-green-400 border border-green-500/30 uppercase">Today</span>
                              )}
                            </div>
                            <div className="text-white/40 text-xs mt-1">
                              {project.project_type === 'volume' ? `$${project.price_per_m3}/m³` : `$${project.price_per_load}/load`}
                              {project.notes && ` • ${project.notes}`}
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 text-xs font-bold uppercase border ${statusConfig.color}`}>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-white/60 text-xs">
                            {project.start_date || '-'}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex flex-wrap gap-0.5">
                              {project.disposal_days?.map(day => (
                                <span key={day} className="px-1 py-0.5 text-xs bg-white/10 text-white/70">
                                  {day}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <span className="text-accent-success font-mono font-bold">{project.calculated_loads_per_day}</span>
                          </td>
                          <td className="py-3 px-2 text-right text-accent-warning font-mono">
                            ${project.daily_revenue.toLocaleString()}
                          </td>
                          <td className="py-3 px-2">
                            {project.progress_percent !== undefined && project.progress_percent !== null ? (
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${project.progress_percent >= 100 ? 'bg-green-500' : 'bg-primary'}`}
                                    style={{ width: `${Math.min(100, project.progress_percent)}%` }}
                                  />
                                </div>
                                <span className="text-xs text-white/60 font-mono">{project.progress_percent}%</span>
                              </div>
                            ) : (
                              <span className="text-white/30 text-xs">-</span>
                            )}
                            {project.calculated_days_remaining !== null && (
                              <div className="text-xs text-white/40 mt-1">
                                {project.calculated_days_remaining} days left
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center justify-center gap-1">
                              {project.status === "active" && (
                                <button
                                  onClick={() => handleStatusChange(project, "paused")}
                                  className="p-1.5 hover:bg-yellow-500/20 transition-colors"
                                  title="Pause project"
                                >
                                  <Pause className="w-4 h-4 text-yellow-400" strokeWidth={2} />
                                </button>
                              )}
                              {project.status === "paused" && (
                                <button
                                  onClick={() => handleStatusChange(project, "active")}
                                  className="p-1.5 hover:bg-green-500/20 transition-colors"
                                  title="Resume project"
                                >
                                  <Play className="w-4 h-4 text-green-400" strokeWidth={2} />
                                </button>
                              )}
                              {project.status !== "completed" && (
                                <button
                                  onClick={() => handleStatusChange(project, "completed")}
                                  className="p-1.5 hover:bg-white/10 transition-colors"
                                  title="Mark complete"
                                >
                                  <CheckCircle className="w-4 h-4 text-white/40 hover:text-green-400" strokeWidth={2} />
                                </button>
                              )}
                              <button
                                onClick={() => handleEdit(project)}
                                className="p-1.5 hover:bg-white/10 transition-colors"
                                data-testid={`edit-project-${project.id}`}
                              >
                                <Edit2 className="w-4 h-4 text-white/60 hover:text-white" strokeWidth={2} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(project)}
                                className="p-1.5 hover:bg-red-500/20 transition-colors"
                                data-testid={`delete-project-${project.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-white/60 hover:text-red-400" strokeWidth={2} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-white/20">
                      <td colSpan={4} className="py-3 px-2 text-right font-heading font-bold text-white uppercase">Today's Total ({summary?.today_day_name})</td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-accent-success font-mono font-bold text-lg">{summary?.today_loads || 0}</span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-accent-warning font-mono font-bold text-lg">${(summary?.today_revenue || 0).toLocaleString()}</span>
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "timeline" && (
        <div className="space-y-6">
          {/* Timeline Chart */}
          <div className="bg-[#161920] border border-white/10 p-6">
            <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">
              Projected Workload - Next 30 Days
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timelineChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
                  <YAxis stroke="#666" tick={{ fill: '#999' }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1d24', border: '1px solid #333', borderRadius: 0, color: '#fff' }} 
                    labelStyle={{ color: '#fff' }} 
                    itemStyle={{ color: '#fff' }}
                    formatter={(value, name) => [value, name === 'loads' ? 'Total Loads' : 'Active Projects']}
                  />
                  <ReferenceLine x={timeline?.today?.slice(5)} stroke="#F97316" strokeDasharray="5 5" label={{ value: 'Today', fill: '#F97316', fontSize: 10 }} />
                  <Area type="monotone" dataKey="loads" stroke="#22C55E" fill="#22C55E" fillOpacity={0.3} name="loads" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Daily Breakdown */}
          <div className="bg-[#161920] border border-white/10 p-6">
            <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Daily Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-3 text-white/60 font-semibold uppercase text-xs">Date</th>
                    <th className="text-left py-2 px-3 text-white/60 font-semibold uppercase text-xs">Day</th>
                    <th className="text-right py-2 px-3 text-white/60 font-semibold uppercase text-xs">Total Loads</th>
                    <th className="text-right py-2 px-3 text-white/60 font-semibold uppercase text-xs">Active Projects</th>
                    <th className="text-left py-2 px-3 text-white/60 font-semibold uppercase text-xs">Project Details</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline?.timeline?.slice(0, 14).map((day, idx) => (
                    <tr key={day.date} className={`border-b border-white/5 ${day.date === timeline?.today ? 'bg-primary/10' : ''}`}>
                      <td className="py-2 px-3 text-white font-mono">
                        {day.date}
                        {day.date === timeline?.today && <span className="ml-2 text-xs text-primary">(Today)</span>}
                      </td>
                      <td className="py-2 px-3 text-white/60">{day.day_name}</td>
                      <td className="py-2 px-3 text-right">
                        <span className={`font-mono font-bold ${day.total_loads > 0 ? 'text-accent-success' : 'text-white/30'}`}>
                          {day.total_loads}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right text-white/60 font-mono">{day.active_projects}</td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {day.projects?.map((p, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-white/10 text-white/70">
                              {p.name}: {p.loads}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d24] border border-white/20 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-heading font-bold text-lg uppercase text-white">
                {editingProject ? 'Edit Project' : 'Add Project'}
              </h3>
              <button
                onClick={() => { setShowAddModal(false); setEditingProject(null); resetForm(); }}
                className="p-1 hover:bg-white/10"
              >
                <X className="w-5 h-5 text-white/60" strokeWidth={2} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Project Name */}
              <div>
                <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white"
                  placeholder="e.g., Team Alliance Admiralty"
                  required
                  data-testid="project-name-input"
                />
              </div>

              {/* Status & Start Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full h-10 px-3 bg-[#161920] border border-white/20 focus:border-primary focus:outline-none text-white"
                    data-testid="status-select"
                  >
                    {PROJECT_STATUSES.map(s => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white [color-scheme:dark]"
                    data-testid="start-date-input"
                  />
                </div>
              </div>

              {/* Project Type */}
              <div>
                <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                  Project Type *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="project_type"
                      value="volume"
                      checked={formData.project_type === 'volume'}
                      onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-white">Volume-based ($/m³)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="project_type"
                      value="load"
                      checked={formData.project_type === 'load'}
                      onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-white">Load-based ($/load)</span>
                  </label>
                </div>
              </div>

              {/* Volume-based fields */}
              {formData.project_type === 'volume' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                        Price per m³ ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_per_m3}
                        onChange={(e) => setFormData({ ...formData, price_per_m3: e.target.value })}
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white"
                        placeholder="45"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                        Total Volume (m³)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.total_volume}
                        onChange={(e) => setFormData({ ...formData, total_volume: e.target.value })}
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white"
                        placeholder="30000"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                        Daily Volume (m³)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.daily_volume}
                        onChange={(e) => setFormData({ ...formData, daily_volume: e.target.value })}
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white"
                        placeholder="250"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                        Volume per Load (m³)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.volume_per_load}
                        onChange={(e) => setFormData({ ...formData, volume_per_load: e.target.value })}
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white"
                        placeholder="7.5"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Load-based fields */}
              {formData.project_type === 'load' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                        Price per Load ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_per_load}
                        onChange={(e) => setFormData({ ...formData, price_per_load: e.target.value })}
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white"
                        placeholder="330"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                        Loads per Day
                      </label>
                      <input
                        type="number"
                        value={formData.loads_per_day}
                        onChange={(e) => setFormData({ ...formData, loads_per_day: e.target.value })}
                        className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white"
                        placeholder="20"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                      Project End Date
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full h-10 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white [color-scheme:dark]"
                    />
                  </div>
                </>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-heading font-bold text-white/60 uppercase mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full h-16 px-3 py-2 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white resize-none"
                  placeholder="Optional notes..."
                />
              </div>

              {/* Disposal Days */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-heading font-bold text-white/60 uppercase">
                    Disposal Days
                  </label>
                  <div className="flex gap-2">
                    <button type="button" onClick={selectAllDays} className="text-xs text-primary hover:text-primary/80 font-medium">
                      All Days
                    </button>
                    <span className="text-white/30">|</span>
                    <button type="button" onClick={selectWeekdays} className="text-xs text-primary hover:text-primary/80 font-medium">
                      Weekdays
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <button
                      key={day.key}
                      type="button"
                      onClick={() => toggleDisposalDay(day.key)}
                      className={`px-3 py-2 text-sm font-bold uppercase border transition-colors ${
                        formData.disposal_days.includes(day.key)
                          ? 'bg-primary border-primary text-white'
                          : 'bg-transparent border-white/20 text-white/40 hover:border-white/40'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-white/40 mt-2">
                  Selected: {formData.disposal_days.length} day{formData.disposal_days.length !== 1 ? 's' : ''}/week
                </p>
              </div>

              {/* Preview */}
              {(formData.daily_volume || formData.loads_per_day) && (
                <div className="bg-accent-success/10 border border-accent-success/30 p-4">
                  <h4 className="font-heading font-bold text-sm uppercase text-accent-success mb-3">
                    <TrendingUp className="w-4 h-4 inline mr-2" />
                    Calculated Values
                  </h4>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    <div className="flex items-center">
                      <span className="text-white/60">Loads/Day:</span>
                      <span className="text-white font-mono font-bold ml-2">{preview.loadsPerDay}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-white/60">Revenue/Day:</span>
                      <span className="text-accent-warning font-mono font-bold ml-2">${preview.dailyRevenue.toLocaleString()}</span>
                    </div>
                    {preview.daysNeeded && (
                      <div className="flex items-center">
                        <span className="text-white/60">Duration:</span>
                        <span className="text-white font-mono font-bold ml-2">{preview.daysNeeded} days</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setEditingProject(null); resetForm(); }}
                  className="flex-1 h-10 bg-transparent border border-white/20 text-white font-heading font-bold text-sm uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 h-10 bg-primary text-white font-heading font-bold text-sm uppercase"
                >
                  {editingProject ? 'Update' : 'Create'} Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d24] border border-white/20 w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-400" strokeWidth={2} />
              <h3 className="font-heading font-bold text-lg text-white">Delete Project</h3>
            </div>
            <p className="text-white/60 mb-6">
              Are you sure you want to delete <span className="text-white font-bold">{deleteConfirm.project_name}</span>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 h-10 bg-transparent border border-white/20 text-white font-heading font-bold text-sm uppercase"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 h-10 bg-red-500 text-white font-heading font-bold text-sm uppercase"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
